import { forwardRef, useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes, cloneElement, isValidElement, type ReactElement } from 'react';
import s from './Form.module.css';
import { Icon } from './Icon';

/** Label + control + hint/error. Wires id/aria-describedby onto the single child control. */
export function Field({ label, hint, error, optional, children, aside, className }: {
  label?: ReactNode; hint?: ReactNode; error?: string | null; optional?: boolean; children: ReactNode; aside?: ReactNode; className?: string;
}) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errId = `${id}-err`;
  const child = isValidElement(children)
    ? cloneElement(children as ReactElement<Record<string, unknown>>, {
        id: (children.props as { id?: string }).id ?? id,
        'aria-invalid': error ? true : undefined,
        'aria-describedby': [hint ? hintId : null, error ? errId : null].filter(Boolean).join(' ') || undefined,
      })
    : children;
  return (
    <div className={[s.field, className].filter(Boolean).join(' ')}>
      {label ? (
        <div className={s.labelRow}>
          <label className={s.label} htmlFor={(isValidElement(children) && (children.props as { id?: string }).id) || id}>
            {label}{optional ? <span className={s.optional}>optional</span> : null}
          </label>
          {aside}
        </div>
      ) : null}
      {child}
      {error ? <div className={s.error} id={errId} role="alert"><Icon name="alert" size={13} />{error}</div> : hint ? <div className={s.hint} id={hintId}>{hint}</div> : null}
    </div>
  );
}

type InputProps = InputHTMLAttributes<HTMLInputElement> & { prefix?: string; suffix?: string; inputSize?: 'sm' };
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input({ prefix, suffix, className, inputSize, ...rest }, ref) {
  const cls = [s.control, inputSize && s.sm, prefix && s.hasPrefix, suffix && s.hasSuffix, className].filter(Boolean).join(' ');
  if (!prefix && !suffix) return <input ref={ref} className={cls} {...rest} />;
  return (
    <div className={s.affixWrap}>
      {prefix ? <span className={s.prefix}>{prefix}</span> : null}
      <input ref={ref} className={cls} {...rest} />
      {suffix ? <span className={s.suffix}>{suffix}</span> : null}
    </div>
  );
});

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement> & { inputSize?: 'sm' }>(function Select({ className, inputSize, children, ...rest }, ref) {
  return <select ref={ref} className={[s.control, inputSize && s.sm, className].filter(Boolean).join(' ')} {...rest}>{children}</select>;
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea({ className, ...rest }, ref) {
  return <textarea ref={ref} className={[s.control, className].filter(Boolean).join(' ')} {...rest} />;
});

export function Checkbox({ label, hint, checked, onChange, disabled, name }: { label: ReactNode; hint?: ReactNode; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean; name?: string }) {
  return (
    <label className={s.check}>
      <input type="checkbox" name={name} checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span>{label}{hint ? <span className={s.checkHint}>{hint}</span> : null}</span>
    </label>
  );
}

export function Toggle({ checked, onChange, label, disabled, 'aria-label': ariaLabel }: { checked: boolean; onChange: (v: boolean) => void; label?: ReactNode; disabled?: boolean; 'aria-label'?: string }) {
  return (
    <label className={s.toggle}>
      <input type="checkbox" role="switch" checked={checked} disabled={disabled} aria-label={ariaLabel} onChange={(e) => onChange(e.target.checked)} />
      <span className={s.track} aria-hidden />
      {label ? <span>{label}</span> : null}
    </label>
  );
}

export function Segmented<V extends string>({ value, onChange, options, 'aria-label': ariaLabel }: {
  value: V; onChange: (v: V) => void; options: { value: V; label: ReactNode }[]; 'aria-label'?: string;
}) {
  return (
    <div className={s.segmented} role="group" aria-label={ariaLabel}>
      {options.map((o) => (
        <button key={o.value} type="button" className={s.segment} aria-pressed={o.value === value} onClick={() => onChange(o.value)}>{o.label}</button>
      ))}
    </div>
  );
}

/** Multi-select chip group. */
export function ChipSelect<V extends string | number>({ options, value, onChange, 'aria-label': ariaLabel }: {
  options: { value: V; label: ReactNode }[]; value: V[]; onChange: (v: V[]) => void; 'aria-label'?: string;
}) {
  return (
    <div className={s.chips} role="group" aria-label={ariaLabel}>
      {options.map((o) => {
        const on = value.includes(o.value);
        return (
          <button key={String(o.value)} type="button" className={s.chip} aria-pressed={on}
            onClick={() => onChange(on ? value.filter((v) => v !== o.value) : [...value, o.value])}>
            {on ? <Icon name="check" size={13} strokeWidth={2.4} /> : null}{o.label}
          </button>
        );
      })}
    </div>
  );
}

/** Big selectable card (radio semantics via aria-pressed). */
export function ChoiceCard({ selected, onClick, icon, title, children, disabled }: { selected: boolean; onClick: () => void; icon?: string; title: ReactNode; children?: ReactNode; disabled?: boolean }) {
  return (
    <button type="button" className={s.radioCard} aria-pressed={selected} onClick={onClick} disabled={disabled}>
      {icon ? <span className={s.radioCardIcon}><Icon name={icon} size={19} /></span> : null}
      <span>
        <span className={s.radioCardTitle}>{title}</span>
        {children ? <span className={s.radioCardBody} style={{ display: 'block' }}>{children}</span> : null}
      </span>
    </button>
  );
}
